import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useCart } from "../context/cartContext";
import ProductItem from "../components/UI/ProductItem";
import { currencyFormatter, getApiUrl, getMediaUrl, getWhatsappUrl, siteConfig } from "../utils/site";
import { AuthContext } from "../context/authContext";
import { FaWhatsapp, FaShippingFast, FaCertificate, FaUndo, FaShieldAlt } from "react-icons/fa";
import TrustBar from "../components/UI/TrustBar";
import "../styles/luxury-product.css";
import "../styles/trust-ui.css";

const parseDescription = (desc) => {
  if (!desc) return [];
  return desc
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
};

const ProductDetails = () => {
  const { id: uuid } = useParams();
  const { addItem } = useCart();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [imageZoomed, setImageZoomed] = useState(false);

  useEffect(() => {
    setLoading(true);

    axios
      .get(getApiUrl(`/api/products/uuid/${uuid}`))
      .then((response) => setProduct(response.data))
      .catch((error) => {
        console.error("Error fetching product:", error);
        setProduct(null);
      })
      .finally(() => setLoading(false));

    axios
      .get(getApiUrl("/api/products"))
      .then((response) => {
        const allProducts = response.data.data || response.data || [];
        setRelatedProducts(
          allProducts.filter((item) => `${item.uuid || item.id}` !== `${uuid}`).slice(0, 4)
        );
      })
      .catch((error) => {
        console.error("Error loading related products:", error);
      });

    window.scrollTo(0, 0);
  }, [uuid]);

  const detailLines = useMemo(() => parseDescription(product?.description), [product]);

  const handleAddToCart = () => {
    if (!user) {
      navigate("/login");
      return;
    }

    addItem(product, quantity);
  };

  const stockNum = Number(product?.stock_quantity || 0);
  const isInStock = stockNum > 0;
  const isLowStock = stockNum > 0 && stockNum <= 5;

  if (loading) {
    return (
      <div className="roha-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="luxury-loading">Loading gemstone details...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="roha-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="luxury-empty-state">
          <h3>Product Not Found</h3>
          <p>This jewelry piece could not be found.</p>
          <Link to="/products" className="roha-button roha-button-primary" style={{ marginTop: '1rem' }}>
            Browse Collection
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{product.name} | Roha Jewellery - Ethiopian Gemstone Jewelry</title>
        <meta
          name="description"
          content={`${product.name} - ${product.description?.substring(0, 155) || 'Luxury Ethiopian gemstone jewelry'}. Ethically sourced, certified authentic. Worldwide shipping available.`}
        />
        <meta name="keywords" content={`${product.name}, Ethiopian ${product.material || 'gemstone'}, ${product.category || 'jewelry'}, luxury jewelry Ethiopia, Roha Jewellery`} />
        <link rel="canonical" href={`${siteConfig.url}/products/${product.uuid || product.id}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`${product.name} | Roha Jewellery`} />
        <meta property="og:description" content={product.description?.substring(0, 155) || 'Luxury Ethiopian gemstone jewelry'} />
        <meta property="og:image" content={getMediaUrl(product.image_url)} />
        <meta property="og:url" content={`${siteConfig.url}/products/${product.uuid || product.id}`} />
        <meta property="og:type" content="product" />
        
        {/* Product Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            image: getMediaUrl(product.image_url),
            description: product.description,
            category: product.category || "Jewelry",
            material: product.material,
            brand: { "@type": "Brand", name: siteConfig.name },
            offers: {
              "@type": "Offer",
              priceCurrency: "ETB",
              price: product.price,
              availability: isInStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              seller: { "@type": "Organization", name: siteConfig.name },
            },
          })}
        </script>
      </Helmet>

      <main className="roha-page">
        <section className="roha-section">
          <Container>
            {/* Breadcrumb */}
            <nav style={{ marginBottom: '2rem', fontSize: '0.9rem', color: 'var(--color-muted)' }}>
              <Link to="/" style={{ color: 'var(--color-muted)' }}>Home</Link>
              <span style={{ margin: '0 0.5rem' }}>/</span>
              <Link to="/products" style={{ color: 'var(--color-muted)' }}>Collection</Link>
              <span style={{ margin: '0 0.5rem' }}>/</span>
              <span style={{ color: 'var(--color-gold)' }}>{product.name}</span>
            </nav>

            <motion.div 
              className="roha-product-detail"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Product Image */}
              <div className="roha-product-detail__media" onClick={() => setImageZoomed(true)}>
                <img
                  src={getMediaUrl(product.image_url)}
                  alt={product.name}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/image.jpg";
                  }}
                />
              </div>

              {/* Product Info */}
              <div className="roha-product-detail__content">
                <span className="roha-eyebrow">{product.category || "Fine Jewelry"}</span>
                <h1>{product.name}</h1>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <p className="roha-product-detail__price">
                    {currencyFormatter.format(Number(product.price || 0))}
                  </p>
                  
                  {/* Stock Badge */}
                  {isInStock ? (
                    <span className={`luxury-stock-badge ${isLowStock ? 'luxury-stock-badge--low-stock' : 'luxury-stock-badge--in-stock'}`}>
                      {isLowStock ? `Only ${stockNum} left` : 'In Stock'}
                    </span>
                  ) : (
                    <span className="luxury-stock-badge luxury-stock-badge--out-of-stock">
                      Out of Stock
                    </span>
                  )}
                </div>

                <p className="roha-product-detail__lead">
                  Handcrafted Ethiopian gemstone jewelry. Each piece is ethically sourced and certified authentic.
                </p>

                {/* Specifications */}
                <div className="luxury-product-specs">
                  {product.material && (
                    <div className="luxury-product-spec">
                      <span className="luxury-product-spec__label">Material</span>
                      <div className="luxury-product-spec__value">{product.material}</div>
                    </div>
                  )}
                  {product.weight && (
                    <div className="luxury-product-spec">
                      <span className="luxury-product-spec__label">Weight</span>
                      <div className="luxury-product-spec__value">{product.weight}g</div>
                    </div>
                  )}
                  {product.size && (
                    <div className="luxury-product-spec">
                      <span className="luxury-product-spec__label">Size</span>
                      <div className="luxury-product-spec__value">{product.size}</div>
                    </div>
                  )}
                  <div className="luxury-product-spec">
                    <span className="luxury-product-spec__label">Origin</span>
                    <div className="luxury-product-spec__value">Ethiopia</div>
                  </div>
                </div>

                {/* Description */}
                {!!detailLines.length && (
                  <div className="roha-product-detail__story">
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>About This Piece</h3>
                    {detailLines.map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                )}

                {/* Quantity Selector */}
                {isInStock && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted)', marginBottom: '0.5rem' }}>
                      Quantity
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1px solid var(--border-subtle)', background: 'var(--surface-glass)', color: 'var(--color-heading)', cursor: 'pointer', fontSize: '1.2rem' }}
                      >
                        −
                      </button>
                      <span style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--color-heading)', minWidth: '40px', textAlign: 'center' }}>
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(Math.min(stockNum, quantity + 1))}
                        style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1px solid var(--border-subtle)', background: 'var(--surface-glass)', color: 'var(--color-heading)', cursor: 'pointer', fontSize: '1.2rem' }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="roha-product-detail__actions">
                  <button
                    type="button"
                    className="roha-button roha-button-primary"
                    onClick={handleAddToCart}
                    disabled={!isInStock}
                    style={{ opacity: isInStock ? 1 : 0.5, cursor: isInStock ? 'pointer' : 'not-allowed', flex: 1 }}
                  >
                    {isInStock ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                  <a
                    href={getWhatsappUrl(`Hello! I'm interested in ${product.name}. Can you provide more details?`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="roha-button roha-button-ghost"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <FaWhatsapp size={18} />
                    WhatsApp Inquiry
                  </a>
                </div>

                {/* Trust Signals */}
                <div className="roha-product-detail__trust">
                  <article>
                    <FaShippingFast size={24} style={{ color: 'var(--color-gold)', marginBottom: '0.5rem' }} />
                    <strong>Worldwide Shipping</strong>
                    <p>Secure insured delivery to your location</p>
                  </article>
                  <article>
                    <FaCertificate size={24} style={{ color: 'var(--color-gold)', marginBottom: '0.5rem' }} />
                    <strong>Certified Authentic</strong>
                    <p>Every gemstone comes with certification</p>
                  </article>
                  <article>
                    <FaUndo size={24} style={{ color: 'var(--color-gold)', marginBottom: '0.5rem' }} />
                    <strong>30-Day Returns</strong>
                    <p>Not satisfied? Full refund within 30 days</p>
                  </article>
                  <article>
                    <FaShieldAlt size={24} style={{ color: 'var(--color-gold)', marginBottom: '0.5rem' }} />
                    <strong>Secure Payment</strong>
                    <p>Your transaction is protected and encrypted</p>
                  </article>
                </div>

                {/* Authenticity badge + delivery indicator */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                  <div className="authenticity-badge" role="status" aria-label="Certified authentic gemstone">
                    <span className="authenticity-badge__icon">✓</span>
                    Certified Authentic Ethiopian Gemstone
                  </div>
                  <div className="delivery-indicator" aria-label="Delivery information">
                    <div className="delivery-indicator__icon"><FaShippingFast /></div>
                    <div className="delivery-indicator__text">
                      <strong>Free insured shipping on orders over ETB 5,000</strong>
                      <span>Estimated delivery: 5–10 business days worldwide</span>
                    </div>
                  </div>
                </div>

                {/* Secure payment icons */}
                <div style={{ marginTop: '1.25rem' }}>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(245,240,232,0.4)', marginBottom: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Secure payment methods
                  </p>
                  <div className="payment-icons" role="list" aria-label="Accepted payment methods">
                    <div className="payment-icon" role="listitem"><i className="ri-lock-line" aria-hidden="true" /> SSL Secured</div>
                    <div className="payment-icon" role="listitem">💳 Chapa</div>
                    <div className="payment-icon" role="listitem">🏦 Bank Transfer</div>
                    <div className="payment-icon" role="listitem">📱 Mobile Money</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </Container>
        </section>

        {/* Related Products */}
        {!!relatedProducts.length && (
          <section className="roha-section" style={{ background: 'var(--surface-glass)' }}>
            <Container>
              <div className="roha-section__heading" style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <p className="roha-eyebrow">You May Also Like</p>
                <h2>Explore Similar Pieces</h2>
              </div>
              <div className="product-grid">
                {relatedProducts.map((item) => (
                  <ProductItem key={item.uuid || item.id} item={item} />
                ))}
              </div>
              <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                <Link to="/products" className="roha-button roha-button-ghost">
                  View Full Collection
                </Link>
              </div>
            </Container>
          </section>
        )}
      </main>

      {/* Image Zoom Modal */}
      {imageZoomed && (
        <div className="luxury-zoom-modal" onClick={() => setImageZoomed(false)}>
          <button className="luxury-zoom-modal__close" onClick={() => setImageZoomed(false)}>
            ×
          </button>
          <img src={getMediaUrl(product.image_url)} alt={product.name} />
        </div>
      )}
    </>
  );
};

export default ProductDetails;
